import React, { useState, useRef, useEffect } from 'react';

interface Point {
  x: number;
  y: number;
}

interface DrawerProps {
  width?: number;
  height?: number;
  onPolygonComplete?: (points: Point[]) => void;
  onPolygonSelected?: (index: number) => void;
  existingPolygons?: Point[][];
  editingPolygonIndex?: number | null;
  className?: string;
  strokeColor?: string;
  fillColor?: string;
  pointRadius?: number;
  strokeWidth?: number;
  fillOpacity?: number;
  isDrawingEnabled?: boolean;
  snapshot?: string;
  zoneNames?: string[];
  onPolygonHover?: (index: number | null) => void;
  onPolygonUpdate?: (points: Point[]) => void;
  drawingPoints?: Point[];
  onDrawingPointsChange?: (points: Point[]) => void;
}

export const Drawer: React.FC<DrawerProps> = ({
  width = 800,
  height = 600,
  onPolygonComplete,
  onPolygonSelected,
  existingPolygons = [],
  editingPolygonIndex = null,
  className = '',
  strokeColor = '#2563eb',
  fillColor = '#2563eb',
  pointRadius = 4,
  strokeWidth = 2,
  fillOpacity = 0.2,
  isDrawingEnabled = false,
  snapshot,
  zoneNames = [],
  onPolygonHover,
  onPolygonUpdate,
  drawingPoints,
  onDrawingPointsChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<Point | null>(null);
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(null);
  const [hoveredLineSegment, setHoveredLineSegment] = useState<{start: number, end: number} | null>(null);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [hoveredPolygonIndex, setHoveredPolygonIndex] = useState<number | null>(null);
  const [currentPointIndex, setCurrentPointIndex] = useState<number | null>(null);

  // Use controlled drawingPoints if provided
  const [internalCurrentPolygon, setInternalCurrentPolygon] = useState<Point[]>([]);
  const currentPolygon = drawingPoints !== undefined ? drawingPoints : internalCurrentPolygon;
  const setCurrentPolygon = (pts: Point[]) => {
    if (onDrawingPointsChange) {
      onDrawingPointsChange(pts);
    } else {
      setInternalCurrentPolygon(pts);
    }
  };

  // Load background image when snapshot changes
  useEffect(() => {
    if (snapshot) {
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        setImageLoaded(true);
        redrawCanvas();
      };
      img.onerror = (err) => {
        console.error('Error loading image:', err);
        setImageLoaded(false);
      };
      
      // Force cache refresh by adding timestamp if not already present
      const imgSrc = snapshot.includes('?') ? snapshot : `${snapshot}?t=${Date.now()}`;
      img.src = imgSrc;
      
      // Force image refresh if src is the same but timestamp parameter changed
      if (imageRef.current && imageRef.current.src.split('?')[0] === imgSrc.split('?')[0]) {
        imageRef.current = null;
        setImageLoaded(false);
      }
    } else {
      imageRef.current = null;
      setImageLoaded(false);
      redrawCanvas();
    }
  }, [snapshot]);

  // Update polygons when existingPolygons prop changes
  useEffect(() => {
    if (editingPolygonIndex !== null) {
      setCurrentPolygon(existingPolygons[editingPolygonIndex] || []);
    }
  }, [existingPolygons, editingPolygonIndex]);

  // Reset drawing state when isDrawingEnabled changes to false
  useEffect(() => {
    if (!isDrawingEnabled && editingPolygonIndex === null) {
      setCurrentPolygon([]);
    }
  }, [isDrawingEnabled, editingPolygonIndex]);

  // Handle updates to the polygon during editing
  const handlePolygonChange = (updatedPoints: Point[]) => {
    if (editingPolygonIndex !== null && onPolygonUpdate) {
      onPolygonUpdate(updatedPoints);
    } else if (onPolygonComplete) {
      onPolygonComplete(updatedPoints);
    }
  };

  const drawPoint = (ctx: CanvasRenderingContext2D, point: Point, isCurrentPolygon: boolean = false) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
    if (isCurrentPolygon) {
      ctx.fillStyle = '#4ade80';
    }
    ctx.fill();
  };

  const drawPolygon = (ctx: CanvasRenderingContext2D, points: Point[], isCurrent: boolean = false, isHovered: boolean = false) => {
    if (points.length < 1) return;

    // Draw fill
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    points.slice(1).forEach(point => {
      ctx.lineTo(point.x, point.y);
    });

    // Only close path and fill for completed polygons or non-editing mode
    if (!isCurrent && points.length > 2) {
      ctx.closePath();
      ctx.globalAlpha = isHovered ? fillOpacity * 1.5 : fillOpacity;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Draw stroke
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    points.slice(1).forEach(point => {
      ctx.lineTo(point.x, point.y);
    });

    // Only close path for completed polygons or non-editing mode
    if (!isCurrent && points.length > 2) {
      ctx.closePath();
    }
    
    // Use a thicker stroke for hovered polygons
    if (isHovered) {
      ctx.lineWidth = strokeWidth + 1;
    }
    
    ctx.stroke();
    
    // Reset stroke width if it was changed
    if (isHovered) {
      ctx.lineWidth = strokeWidth;
    }

    // Draw points
    points.forEach(point => drawPoint(ctx, point, isCurrent));
  };

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background image if available
    if (imageRef.current && imageLoaded) {
      const img = imageRef.current;
      // Draw the image to fit the canvas while maintaining aspect ratio
      const imgRatio = img.width / img.height;
      const canvasRatio = canvas.width / canvas.height;
      
      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let offsetX = 0;
      let offsetY = 0;
      
      if (imgRatio > canvasRatio) {
        // Image is wider than canvas (relatively)
        drawHeight = canvas.width / imgRatio;
        offsetY = (canvas.height - drawHeight) / 2;
      } else {
        // Image is taller than canvas (relatively)
        drawWidth = canvas.height * imgRatio;
        offsetX = (canvas.width - drawWidth) / 2;
      }
      
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
    }

    // Set styles for existing polygons
    ctx.strokeStyle = strokeColor;
    ctx.fillStyle = fillColor;
    ctx.lineWidth = strokeWidth;

    // Draw completed polygons
    existingPolygons.forEach((polygon, index) => {
      if (index !== editingPolygonIndex) {
        const isHovered = index === hoveredPolygonIndex;
        drawPolygon(ctx, polygon, false, isHovered);
      }
    });

    // Set styles for current/editing polygon
    ctx.strokeStyle = '#4ade80';
    ctx.fillStyle = '#4ade80';
    ctx.lineWidth = strokeWidth;

    // Draw current polygon
    if (currentPolygon.length > 0) {
      drawPolygon(ctx, currentPolygon, true);

      // Draw line to mouse if hovering
      if (hoveredPoint && !isDrawingEnabled && editingPolygonIndex === null) {
        ctx.beginPath();
        ctx.moveTo(currentPolygon[currentPolygon.length - 1].x, currentPolygon[currentPolygon.length - 1].y);
        ctx.lineTo(hoveredPoint.x, hoveredPoint.y);
        ctx.stroke();

        // In drawing mode, also show a line back to the start point if near it
        const startPoint = currentPolygon[0];
        if (currentPolygon.length > 2 && isPointNearPoint(hoveredPoint, startPoint, 15)) {
          ctx.beginPath();
          ctx.moveTo(hoveredPoint.x, hoveredPoint.y);
          ctx.lineTo(startPoint.x, startPoint.y);
          ctx.setLineDash([3, 3]);
          ctx.stroke();
          ctx.setLineDash([]);

          // Highlight start point
          ctx.beginPath();
          ctx.arc(startPoint.x, startPoint.y, pointRadius + 3, 0, 2 * Math.PI);
          ctx.strokeStyle = 'rgba(74, 222, 128, 0.8)';
          ctx.stroke();
        }
      }
    }
  };

  useEffect(() => {
    redrawCanvas();
  }, [currentPolygon, existingPolygons, hoveredPoint, editingPolygonIndex, hoveredPolygonIndex, imageLoaded]);

  const isPointNearPoint = (p1: Point, p2: Point, threshold: number = 10) => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy) <= threshold;
  };

  const pointToSegmentDistance = (p: Point, a: Point, b: Point): number => {
    const A = p.x - a.x;
    const B = p.y - a.y;
    const C = b.x - a.x;
    const D = b.y - a.y;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    const param = len_sq !== 0 ? dot / len_sq : -1;

    let xx, yy;
    if (param < 0) {
      xx = a.x;
      yy = a.y;
    } else if (param > 1) {
      xx = b.x;
      yy = b.y;
    } else {
      xx = a.x + param * C;
      yy = a.y + param * D;
    }

    const dx = p.x - xx;
    const dy = p.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Add line intersection detection utility function
  const doLinesIntersect = (a: Point, b: Point, c: Point, d: Point): boolean => {
    // Line AB represented as a1x + b1y = c1
    const a1 = b.y - a.y;
    const b1 = a.x - b.x;
    const c1 = a1 * a.x + b1 * a.y;
 
    // Line CD represented as a2x + b2y = c2
    const a2 = d.y - c.y;
    const b2 = c.x - d.x;
    const c2 = a2 * c.x + b2 * c.y;
 
    const determinant = a1 * b2 - a2 * b1;
 
    if (determinant === 0) {
      // Lines are parallel
      return false;
    }
 
    const x = (b2 * c1 - b1 * c2) / determinant;
    const y = (a1 * c2 - a2 * c1) / determinant;
 
    // Check if the intersection point is on both line segments
    const onSegment1 = 
      x >= Math.min(a.x, b.x) && 
      x <= Math.max(a.x, b.x) && 
      y >= Math.min(a.y, b.y) && 
      y <= Math.max(a.y, b.y);
    
    const onSegment2 = 
      x >= Math.min(c.x, d.x) && 
      x <= Math.max(c.x, d.x) && 
      y >= Math.min(c.y, d.y) && 
      y <= Math.max(c.y, d.y);
 
    // Exclude endpoints - allow adjacent segments to share endpoints
    if (
      (x === a.x && y === a.y) || 
      (x === b.x && y === b.y) || 
      (x === c.x && y === c.y) || 
      (x === d.x && y === d.y)
    ) {
      return false;
    }
 
    return onSegment1 && onSegment2;
  };

  // Check if adding a point would create intersecting lines
  const wouldCreateIntersection = (points: Point[], newPoint: Point, insertIndex: number): boolean => {
    if (points.length < 2) return false;

    // Create the new potential polygon
    const newPoints = [...points];
    newPoints.splice(insertIndex, 0, newPoint);

    // Check each segment against other segments
    for (let i = 0; i < newPoints.length; i++) {
      const i1 = i;
      const i2 = (i + 1) % newPoints.length;
      const seg1Start = newPoints[i1];
      const seg1End = newPoints[i2];

      for (let j = i + 2; j < newPoints.length; j++) {
        // Skip adjacent segments
        if (i2 === j || i1 === (j + 1) % newPoints.length) continue;

        const j1 = j;
        const j2 = (j + 1) % newPoints.length;
        const seg2Start = newPoints[j1];
        const seg2End = newPoints[j2];

        if (doLinesIntersect(seg1Start, seg1End, seg2Start, seg2End)) {
          return true;
        }
      }
    }
    return false;
  };

  // Modify insertPointAtCorrectPosition to allow more flexible point placement during editing
  const insertPointAtCorrectPosition = (newPoint: Point, existingPoints: Point[]): Point[] => {
    if (existingPoints.length < 2) return [...existingPoints, newPoint];
    
    // When in editing mode, we'll add the point based on the nearest line segment
    let bestIndex = -1;
    let minDistance = Infinity;

    for (let i = 0; i < existingPoints.length - 1; i++) {
      const current = existingPoints[i];
      const next = existingPoints[i + 1];

      const distance = pointToSegmentDistance(newPoint, current, next);
      
      // Only consider this position if it won't create an intersection
      if (distance < minDistance && !wouldCreateIntersection(existingPoints, newPoint, i + 1)) {
        minDistance = distance;
        bestIndex = i + 1;
      }
    }

    // If no valid position was found along the path
    if (bestIndex === -1) {
      // Add to the end if it's a reasonable point
      if (existingPoints.length > 0) {
        const lastPoint = existingPoints[existingPoints.length - 1];
        const distance = Math.sqrt(
          Math.pow(newPoint.x - lastPoint.x, 2) + Math.pow(newPoint.y - lastPoint.y, 2)
        );
        
        // If it's close enough to the last point, add it to the end
        if (distance < 150) {
          return [...existingPoints, newPoint];
        }
      }
      return existingPoints; // Return unchanged
    }

    const newPoints = [...existingPoints];
    newPoints.splice(bestIndex, 0, newPoint);
    return newPoints;
  };

  // Check if a point is inside a polygon
  const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
    if (polygon.length < 3) return false;
    
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y))
          && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    
    return inside;
  };

  // Modify handleMouseMove for dragging points to prevent intersections
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const mousePoint = { x, y };

    setHoveredPoint(mousePoint);

    // Check if mouse is hovering over a polygon (only when not drawing or editing)
    if (!isDrawingEnabled && editingPolygonIndex === null) {
      let foundHoveredPolygon = false;
      for (let i = 0; i < existingPolygons.length; i++) {
        if (isPointInPolygon(mousePoint, existingPolygons[i])) {
          setHoveredPolygonIndex(i);
          canvas.style.cursor = 'pointer';
          foundHoveredPolygon = true;
          onPolygonHover?.(i);
          break;
        }
      }
      
      if (!foundHoveredPolygon) {
        setHoveredPolygonIndex(null);
        canvas.style.cursor = 'default';
        onPolygonHover?.(null);
      }
    }

    if (editingPolygonIndex !== null) {
      // Check if mouse is near any existing point
      const nearPointIndex = currentPolygon.findIndex(point => 
        isPointNearPoint(mousePoint, point)
      );

      if (nearPointIndex !== -1) {
        canvas.style.cursor = 'move';
        if (draggedPointIndex !== null) {
          // Create a copy of points for testing
          const testPolygon = [...currentPolygon];
          testPolygon[draggedPointIndex] = mousePoint;
          
          // Check if moving this point would create intersections
          let wouldIntersect = false;
          
          for (let i = 0; i < testPolygon.length; i++) {
            // The two segments that include the moved point
            const prevIdx = (i === 0) ? testPolygon.length - 1 : i - 1;
            const nextIdx = (i + 1) % testPolygon.length;
            
            if (i === draggedPointIndex || prevIdx === draggedPointIndex) {
              // Check this segment against all non-adjacent segments
              const segStart = testPolygon[i];
              const segEnd = testPolygon[nextIdx];
              
              for (let j = 0; j < testPolygon.length; j++) {
                const j2 = (j + 1) % testPolygon.length;
                
                // Skip if this is the segment we're checking or an adjacent one
                if (j === i || j === prevIdx || j2 === i || j2 === nextIdx) continue;
                
                if (doLinesIntersect(segStart, segEnd, testPolygon[j], testPolygon[j2])) {
                  wouldIntersect = true;
                  break;
                }
              }
            }
            
            if (wouldIntersect) break;
          }
          
          if (!wouldIntersect) {
            const newPoints = [...currentPolygon];
            newPoints[draggedPointIndex] = mousePoint;
            setCurrentPolygon(newPoints);
            handlePolygonChange(newPoints);
          }
        }
        return;
      }

      // Check if mouse is near any line segment in the current polygon
      if (currentPolygon.length > 1) {
        let minDistance = Infinity;
        let closestSegment = null;
        
        for (let i = 0; i < currentPolygon.length; i++) {
          const currentPoint = currentPolygon[i];
          const nextPoint = currentPolygon[(i + 1) % currentPolygon.length];
          
          const distance = pointToSegmentDistance(mousePoint, currentPoint, nextPoint);
          if (distance < 10 && distance < minDistance) { // 10px threshold
            // Check if adding a point here would create intersections
            const testPoints = [...currentPolygon];
            testPoints.splice((i + 1) % currentPolygon.length, 0, mousePoint);
            
            let wouldIntersect = false;
            for (let j = 0; j < testPoints.length; j++) {
              const j2 = (j + 1) % testPoints.length;
              for (let k = j + 2; k < testPoints.length; k++) {
                const k2 = (k + 1) % testPoints.length;
                if (k2 === j) continue; // Skip adjacent segments
                
                if (doLinesIntersect(testPoints[j], testPoints[j2], testPoints[k], testPoints[k2])) {
                  wouldIntersect = true;
                  break;
                }
              }
              if (wouldIntersect) break;
            }
            
            if (!wouldIntersect) {
              minDistance = distance;
              closestSegment = { start: i, end: (i + 1) % currentPolygon.length };
            }
          }
        }
        
        if (closestSegment) {
          setHoveredLineSegment(closestSegment);
          canvas.style.cursor = 'pointer';
          return;
        }
      }
      
      setHoveredLineSegment(null);
      canvas.style.cursor = 'default';
      
    } else if (isDrawingEnabled) {
      // Check if near any point in the current polygon
      const nearPointIndex = currentPolygon.findIndex(point => 
        isPointNearPoint(mousePoint, point)
      );
      
      if (nearPointIndex !== -1) {
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = 'crosshair';
      }
    } else {
      canvas.style.cursor = 'default';
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (editingPolygonIndex === null) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const mousePoint = { x, y };

    // Check if mouse is near any existing point
    const nearPointIndex = currentPolygon.findIndex(point => 
      isPointNearPoint(mousePoint, point)
    );

    if (nearPointIndex !== -1) {
      setDraggedPointIndex(nearPointIndex);
    }
  };

  const handleMouseUp = () => {
    setDraggedPointIndex(null);
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
    setDraggedPointIndex(null);
    setHoveredLineSegment(null);
    setHoveredPolygonIndex(null);
    onPolygonHover?.(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
  };

  // Update handleCanvasClick to handle edit mode similar to create mode
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingEnabled && editingPolygonIndex === null) {
      // Check if clicking on an existing polygon to select it
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;
      const clickedPoint = { x, y };

      // Check if the click is inside any polygon
      for (let i = 0; i < existingPolygons.length; i++) {
        if (isPointInPolygon(clickedPoint, existingPolygons[i])) {
          onPolygonSelected?.(i);
          return;
        }
      }
      
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const clickedPoint = { x, y };

    // Handle double click to delete point
    const currentTime = Date.now();
    if (currentTime - lastClickTime < 300) { // 300ms threshold for double click
      const nearPointIndex = currentPolygon.findIndex(point => isPointNearPoint(clickedPoint, point));
      if (nearPointIndex !== -1 && currentPolygon.length > 3) {
        const newPoints = [...currentPolygon];
        newPoints.splice(nearPointIndex, 1);
        
        // Check if removing the point would create intersections
        let wouldIntersect = false;
        for (let i = 0; i < newPoints.length - 1; i++) {
          for (let j = i + 2; j < newPoints.length - 1; j++) {
            if (doLinesIntersect(
              newPoints[i], 
              newPoints[i + 1], 
              newPoints[j], 
              newPoints[j + 1]
            )) {
              wouldIntersect = true;
              break;
            }
          }
          if (wouldIntersect) break;
        }
        
        if (!wouldIntersect) {
          setCurrentPolygon(newPoints);
          handlePolygonChange(newPoints);
        }
        setLastClickTime(0); // Reset click time
        return;
      }
    }
    setLastClickTime(currentTime);

    // Check if we're near any existing point in the current polygon
    const nearPointIndex = currentPolygon.findIndex(point => isPointNearPoint(clickedPoint, point));
    const nearExistingPoint = nearPointIndex !== -1;
    const isNearStartPoint = currentPolygon.length > 0 && isPointNearPoint(clickedPoint, currentPolygon[0]);
    
    // Unified logic for both initial drawing and editing mode
    if (isDrawingEnabled || editingPolygonIndex !== null) {
      // Check if clicking near the start point to close the polygon
      if (isNearStartPoint && currentPolygon.length > 2) {
        // Verify that closing the polygon won't create intersections
        const tempPolygon = [...currentPolygon];
        let wouldIntersect = false;
        
        // Check if the closing segment would intersect with any non-adjacent segments
        for (let i = 1; i < tempPolygon.length - 1; i++) {
          if (doLinesIntersect(
            tempPolygon[tempPolygon.length - 1], 
            tempPolygon[0], 
            tempPolygon[i], 
            tempPolygon[i + 1]
          )) {
            wouldIntersect = true;
            break;
          }
        }
        
        if (!wouldIntersect) {
          onPolygonComplete?.([...currentPolygon]);
          if (!editingPolygonIndex) {
            setCurrentPolygon([]);
          }
        }
        return;
      }
      
      // If not near start point and not near other existing points, add a new point
      if (!nearExistingPoint) {
        // Check if adding would create intersections
        const tempPolygon = [...currentPolygon];
        let wouldIntersect = false;
        
        if (tempPolygon.length >= 2) {
          // Check if the new segment would intersect with any non-adjacent segments
          for (let i = 0; i < tempPolygon.length - 2; i++) {
            if (doLinesIntersect(
              tempPolygon[tempPolygon.length - 1], 
              clickedPoint, 
              tempPolygon[i], 
              tempPolygon[i + 1]
            )) {
              wouldIntersect = true;
              break;
            }
          }
        }
        
        if (!wouldIntersect) {
          const newPoints = [...currentPolygon, clickedPoint];
          setCurrentPolygon(newPoints);
          if (editingPolygonIndex !== null) {
            handlePolygonChange(newPoints);
          }
        }
      }
      return;
    }

    // Initial point for starting a new polygon
    setCurrentPolygon([clickedPoint]);
  };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      onClick={handleCanvasClick}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      className={`border border-gray-200 rounded-lg ${className}`}
    />
  );
};
